/**
 * Home page - Landing page with module links
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';

const Home = () => {
  const { isManager, isAdmin } = useUser();
  const { isAuthenticated, login, resetAndLogin } = useAuth();

  const modules = [
    {
      title: 'Preferred Ship-From Override',
      description: 'Manage preferred source plants and transport modes for 400,000+ material/customer combinations',
      path: '/preferred-ship-from',
      icon: '🚚',
      show: true,
    },
    {
      title: 'Material Periodic Review',
      description: 'Review and edit material/plant master data for 300,000+ combinations with role-based access',
      path: '/material-review',
      icon: '📋',
      show: true,
    },
    {
      title: 'Admin Dashboard',
      description: 'View metrics, late records reporting, and user activity tracking',
      path: '/admin',
      icon: '📊',
      show: isManager || isAdmin,
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Welcome to IBP Data Management</h1>
        <p className="mt-2 text-lg text-gray-600">
          {isAuthenticated
            ? "Select a module below to get started"
            : "Initializing authentication..."}
        </p>
      </div>

      {!isAuthenticated && (
        <div className="flex flex-col justify-center items-center my-12">
          <div className="animate-pulse text-gray-600 mb-6">
            Redirecting to Microsoft login...
          </div>

          {/* Show manual login option after 5 seconds */}
          {(() => {
            const authAttemptTimestamp = sessionStorage.getItem('msal_auth_attempt');
            const currentTime = Date.now();
            if (authAttemptTimestamp && (currentTime - parseInt(authAttemptTimestamp) > 5000)) {
              return (
                <div className="text-center">
                  <div className="mb-2 text-sm text-gray-600">
                    If you're not automatically redirected:
                  </div>
                  <button
                    onClick={resetAndLogin}
                    className="px-4 py-2 bg-eastman-blue text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Login with Microsoft
                  </button>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {isAuthenticated && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map(
            (module) =>
              module.show && (
                <Link
                  key={module.path}
                  to={module.path}
                  className="block p-6 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-4">{module.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{module.title}</h3>
                  <p className="text-gray-600">{module.description}</p>
                </Link>
              )
          )}
        </div>
      )}

      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-2">Getting Started</h2>
        <ul className="list-disc list-inside text-blue-800 space-y-1">
          <li>Use the navigation menu to access different modules</li>
          <li>Filters are searchable dropdowns that update dynamically</li>
          <li>Click on table cells to edit values inline</li>
          <li>Changes must be saved before navigating away</li>
        </ul>
      </div>

      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Reference Guides</h2>
        <div className="space-y-2">
          <div>
            <a
              href="https://eastmanchem.sharepoint.com/:p:/r/sites/DataManagement/Shared%20Documents/QRGs/IBP%20Material%20Periodic%20Review%20QRG.pptx?d=w8ce279a7f44f48279ad5d58c3909b452&csf=1&web=1&e=dQqj3C"
              target="_blank"
              rel="noopener noreferrer"
              className="text-eastman-blue hover:text-blue-700 underline inline-flex items-center"
            >
              📋 Material Periodic Review - Quick Reference Guide
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          <div>
            <a
              href="https://eastmanchem.sharepoint.com/:p:/r/sites/DataManagement/Shared%20Documents/QRGs/IBP%20Material%20Periodic%20Review%20QRG.pptx?d=w8ce279a7f44f48279ad5d58c3909b452&csf=1&web=1&e=dQqj3C"
              target="_blank"
              rel="noopener noreferrer"
              className="text-eastman-blue hover:text-blue-700 underline inline-flex items-center"
            >
              🚚 Preferred Ship-From Override - Quick Reference Guide
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
